import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react'
import { useIntervalloAttivo } from '../../servizi/sessione.tsx'
import type { Cartella, VoceThread } from '../../servizi/posta.ts'
import * as continua from '../../servizi/lettura-continua.ts'
import '../../stile-posta-continua.css'

/**
 * Lettura continua mentre la pagina Posta e' aperta (31/10/2026).
 *
 * Due temporizzatori, entrambi con useIntervalloAttivo: girano solo a pagina visibile e con
 * qualcuno che la usa, e si fermano dopo mezz'ora senza un tocco.
 *
 *  - ogni minuto si chiede all'API di rileggere le caselle guardate (useLetturaContinua). L'API
 *    accoda la lettura solo se serve e dice a che punto e' ciascuna casella;
 *  - ogni trenta secondi si rilegge dal database la prima pagina dell'elenco e i conteggi delle
 *    cartelle (useRiletturaElenco). Quando una casella risulta appena letta, la rilettura parte
 *    subito.
 *
 * Senza l'avvio immediato del Job (`LAVORI_JOB_*` assenti) i lavori aspettano il giro orario: la
 * riga dice «aggiornata alle …» con l'ora vera dell'ultima lettura, e nessun indicatore di lettura
 * in corso finche' il Job non la prende davvero.
 */

const OGNI_MINUTO = 60_000
const OGNI_MEZZO_MINUTO = 30_000
/** Due richieste per le stesse caselle a meno di cosi' sono la stessa: l'avvio e il cambio di vista. */
const DOPPIONE_MS = 5_000

export type LetturaContinua = {
  stati: ReadonlyMap<string, continua.StatoLettura>
  avvioImmediato: boolean
  modo: continua.EsitoLettura['modo'] | undefined
}

/**
 * `caselle`: gli identificativi guardati, `'mie'` per la propria e quelle di gruppo, null per niente.
 * `letta` si chiama quando una casella ha un'ultima lettura piu' recente di quella vista prima.
 */
export function useLetturaContinua(
  caselle: string[] | 'mie' | null, letta?: () => void,
): LetturaContinua {
  const [stati, setStati] = useState<ReadonlyMap<string, continua.StatoLettura>>(new Map())
  const [avvioImmediato, setAvvioImmediato] = useState(false)
  const [modo, setModo] = useState<continua.EsitoLettura['modo'] | undefined>(undefined)
  const chiave = caselle === null ? null : caselle === 'mie' ? 'mie' : [...caselle].sort().join(',')
  const ultima = useRef<{ chiave: string; quando: number } | null>(null)
  const avvisa = useRef(letta)
  avvisa.current = letta

  const chiedi = useCallback(async () => {
    if (chiave === null || chiave === '') return
    if (ultima.current?.chiave === chiave && Date.now() - ultima.current.quando < DOPPIONE_MS) return
    ultima.current = { chiave, quando: Date.now() }
    try {
      const esito = await continua.chiediLettura(chiave === 'mie' ? undefined : chiave.split(','))
      setModo(esito.modo)
      setAvvioImmediato(esito.avvio_immediato)
      setStati((precedenti) => {
        const nuovi = new Map(precedenti)
        let novita = false
        for (const s of esito.caselle) {
          const prima = precedenti.get(s.id)?.ultima_lettura
          if (s.ultima_lettura && prima && s.ultima_lettura > prima) novita = true
          nuovi.set(s.id, s)
        }
        if (novita) queueMicrotask(() => avvisa.current?.())
        return nuovi
      })
    } catch {
      // Una richiesta automatica andata male non deve disturbare: riprova al giro dopo.
    }
  }, [chiave])

  // Cambiando casella o vista si chiede subito, senza aspettare il minuto.
  useEffect(() => { void chiedi() }, [chiedi])
  // A lettura spenta (modo null) non si insiste: l'interruttore si accende di rado, e ricaricando
  // la pagina il controllo riparte.
  useIntervalloAttivo(() => { void chiedi() }, OGNI_MINUTO, chiave !== null && chiave !== '' && modo !== null)

  return { stati, avvioImmediato, modo }
}

/**
 * Rilettura leggera dell'elenco: la prima pagina con i filtri della pagina, dal database.
 *
 * `filtro` nullo la sospende: con la ricerca attiva (ogni pagina va decifrata e filtrata) e finche'
 * le cartelle della casella non sono arrivate. `richiesta` e' il contatore della pagina: se nel
 * frattempo la persona ha cambiato casella o cartella, il risultato e' vecchio e si scarta.
 */
export function useRiletturaElenco(p: {
  filtro: continua.Rilettura | null
  richiesta: MutableRefObject<number>
  voci: VoceThread[]
  cursore: string | null
  applica: (voci: VoceThread[], cursore: string | null) => void
  cartelle: (cartelle: Cartella[]) => void
}): () => void {
  const corrente = useRef(p)
  corrente.current = p
  const inVolo = useRef(false)

  const rileggi = useCallback(() => {
    const { filtro, richiesta } = corrente.current
    if (!filtro || inVolo.current) return
    const mia = richiesta.current
    inVolo.current = true
    void Promise.all([
      continua.rileggiElenco(filtro),
      filtro.casellaId ? continua.rileggiCartelle(filtro.casellaId).catch(() => null) : Promise.resolve(null),
    ]).then(([pagina, cartelle]) => {
      const ora = corrente.current
      if (mia !== ora.richiesta.current || JSON.stringify(ora.filtro) !== JSON.stringify(filtro)) return
      const unite = continua.unisciPagina(ora.voci, ora.cursore, pagina, filtro.limite)
      ora.applica(unite.voci, unite.cursore)
      if (cartelle) ora.cartelle(cartelle)
    }).catch(() => undefined).finally(() => { inVolo.current = false })
  }, [])

  // Il primo giro del temporizzatore cade all'apertura, quando la pagina ha appena caricato
  // l'elenco da se': si salta.
  const primo = useRef(true)
  useIntervalloAttivo(() => {
    if (primo.current) { primo.current = false; return }
    rileggi()
  }, OGNI_MEZZO_MINUTO, p.filtro !== null)

  return rileggi
}

const formatoOra = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' })
const giornoEOra = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
})

function quando(iso: string): string {
  const d = new Date(iso)
  const oggi = d.toDateString() === new Date().toDateString()
  return oggi ? `alle ${formatoOra.format(d)}` : `il ${giornoEOra.format(d).replace(', ', ' alle ')}`
}

/**
 * Piu' caselle in una riga sola, per la pagina a webmail spenta: l'ora e' quella della casella letta
 * meno di recente, cosi' «aggiornata alle» vale per tutte.
 */
export function unisciStati(stati: ReadonlyMap<string, continua.StatoLettura>): continua.StatoLettura | undefined {
  const leggibili = [...stati.values()].filter((s) => s.leggibile)
  if (!leggibili.length) return undefined
  const ore = leggibili.map((s) => s.ultima_lettura)
  return {
    id: 'tutte',
    leggibile: true,
    ultima_lettura: ore.includes(null) ? null : (ore as string[]).sort()[0]!,
    errore: leggibili.some((s) => s.errore),
    in_coda: leggibili.some((s) => s.in_coda),
    in_lettura: leggibili.some((s) => s.in_lettura),
    accodata: leggibili.some((s) => s.accodata),
  }
}

/** La riga discreta sotto la casella: quando e' stata letta, o che la si sta leggendo. */
export function StatoLettura({ stato, avvioImmediato }: {
  stato: continua.StatoLettura | undefined; avvioImmediato: boolean
}) {
  if (!stato?.leggibile) return null
  const titolo = avvioImmediato
    ? 'Mentre la pagina è aperta la casella si rilegge ogni minuto.'
    : 'Le caselle si leggono al giro orario dei lavori.'
  // «In lettura» solo se qualcuno la sta leggendo davvero, o la leggera' fra pochi secondi.
  if (stato.in_lettura || (stato.in_coda && avvioImmediato)) {
    return (
      <span className="posta-lettura-stato" title={titolo}>
        <span className="rotella" aria-hidden="true" /> lettura in corso…
      </span>
    )
  }
  return (
    <span className={`posta-lettura-stato${stato.errore ? ' errore' : ''}`} title={titolo}>
      {stato.ultima_lettura
        ? `${stato.errore ? 'lettura non riuscita' : 'aggiornata'} ${quando(stato.ultima_lettura)}`
        : 'non ancora letta'}
    </span>
  )
}
